import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useLocation } from "@reach/router";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { EnvelopeOpenIcon } from "@heroicons/react/24/solid";
import PopUpImage from "./PopUpImage";
import useMatomoEvent from "./hooks/useMatomoEvent";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  Fragment,
} from "react";

export default function ExitIntentPopup({ title }) {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const trackMatomo = useMatomoEvent();

  const handleClose = useCallback(() => {
    setVisible(false);
    document.body.classList.remove("overflow-hidden");
    trackMatomo("Exit Popup", "Cerrado", location.pathname);
  }, [trackMatomo, location.pathname]);

  // Exit intent en desktop
  useEffect(() => {
    if (typeof window === "undefined") return;

    let lastY = window.innerHeight;
    const handleMouseMove = (e) => {
      const alreadyShown = sessionStorage.getItem("exit-popup-shown");
      const isDesktop = window.innerWidth > 768;

      if (e.clientY < 5 && e.clientY < lastY && !alreadyShown && isDesktop) {
        setVisible(true);
        sessionStorage.setItem("exit-popup-shown", "true");
        document.body.classList.add("overflow-hidden");
        setTimeout(() => inputRef.current?.focus(), 100);
        trackMatomo("Exit Popup", "Visto", `Auto ${location.pathname}`);
      }
      lastY = e.clientY;
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [trackMatomo, location.pathname]);

  // Scroll + tiempo en móvil
  useEffect(() => {
    if (typeof window === "undefined") return;

    const alreadyShown = sessionStorage.getItem("exit-popup-shown");
    const isMobile = window.innerWidth <= 768;
    let timer = null;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolledRatio = scrollY / documentHeight;

      if (scrolledRatio >= 0.3 && !alreadyShown && isMobile) {
        window.removeEventListener("scroll", handleScroll);
        timer = setTimeout(() => {
          setVisible(true);
          sessionStorage.setItem("exit-popup-shown", "true");
          document.body.classList.add("overflow-hidden");
          setTimeout(() => inputRef.current?.focus(), 100);
          trackMatomo(
            "Exit Popup",
            "Visto",
            `Móvil (scroll+tiempo) ${location.pathname}`
          );
        }, 8000);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timer) clearTimeout(timer);
    };
  }, [trackMatomo, location.pathname]);

  const validateContact = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s+\-()]{6,}$/;
    return emailRegex.test(value) || phoneRegex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const contact = form.contacto.value.trim();

    if (!validateContact(contact)) {
      setError("Por favor, introduce un email o teléfono válido.");
      return;
    }

    const formData = new FormData(form);
    const encoded = new URLSearchParams(formData).toString();

    console.log("📨 Enviando formulario con datos:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded,
      });
      setSubmitted(true);
      form.reset();
      setError(null);

      trackMatomo("Exit Popup", "Enviado", location.pathname);
      if (typeof window !== "undefined" && window.splitbee) {
        window.splitbee.track("Popup Enviado", {
          campo: "email o teléfono",
          page: location.pathname,
        });
      }
    } catch (err) {
      console.error("Error al enviar:", err);
      setError("Hubo un error al enviar. Inténtalo más tarde.");
    }
  };

  return (
    <Transition show={visible} as={Fragment} appear>
      <Dialog onClose={handleClose} className="relative z-50">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel
              onClick={(e) => e.stopPropagation()}
              className=" max-w-sm md:max-w-5xl w-full relative flex flex-col md:flex-row justify-between items-center bg-white rounded-3xl shadow"
            >
              {/* Cerrar */}
              <button
                onClick={handleClose}
                className="absolute top-0 right-0 p-2 rounded-tr-3xl rounded-bl-3xl bg-blue-500 text-white hover:bg-blue-700 duration-200"
              >
                <XMarkIcon className="size-6" />
                <span className="sr-only">Cerrar</span>
              </button>

              {/* Contenido */}
              <div className="px-8 pt-4 pb-6 md:py-0 md:px-8 lg:p-12">
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 lg:mb-8">
                  <span className="text-blue-500">No cierres tu terraza</span>{" "}
                  sin hablar con nosotros antes
                </h2>
                <p className="mb-4 text-sm md:text-base">
                  No empieces tu proyecto{" "}
                  <span className="hidden md:inline">
                    de cerramientos para tu terraza, ático, patio, porche o
                    local
                  </span>{" "}
                  sin ponerte en contacto con nosotros. Déjanos tu email o
                  teléfono y{" "}
                  <strong className="text-blue-500">
                    te asesoramos sin compromiso
                  </strong>
                  .
                </p>

                {!submitted ? (
                  <form
                    name="exit-intent"
                    method="POST"
                    data-netlify="true"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-3"
                  >
                    <input type="hidden" name="form-name" value="exit-intent" />
                    <input
                      type="hidden"
                      name="Página de salida"
                      value={title}
                    />
                    <div className="flex">
                      <input
                        type="text"
                        name="contacto"
                        required
                        ref={inputRef}
                        placeholder="Tu email o teléfono"
                        className="md:flex-1 border border-blue-600 border-r-0 rounded-l-md text-sm py-2 px-2 md:py-3 md:px-3 focus:outline-none focus:ring-0"
                      />
                      <button
                        type="submit"
                        className="rounded-r-md bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 font-medium"
                      >
                        <span className="md:hidden">Enviar</span>
                        <span className="hidden md:inline-block">
                          Ayudadme a elegir
                        </span>
                      </button>
                    </div>
                    {error && (
                      <p className="text-red-500 text-sm font-medium">
                        {error}
                      </p>
                    )}
                  </form>
                ) : (
                  <p className="flex items-center bg-green-500 border border-green-500 rounded-md text-white text-sm py-2 px-3 md:py-3 md:px-3">
                    <strong className="font-bold flex items-center gap-1">
                      <EnvelopeOpenIcon className="size-3" /> Mensaje enviado
                    </strong>
                    : te contactaremos muy pronto.
                  </p>
                )}
              </div>

              {/* Imagen */}
              <div className="shrink-0 md:max-w-xs lg:max-w-sm xl:max-w-md">
                <PopUpImage className="rounded-b-3xl aspect-[4/3] md:aspect-square md:rounded-bl-none md:rounded-r-3xl" />
              </div>
              <button
                onClick={handleClose}
                className="absolute top-0 right-0 p-2 rounded-tr-3xl rounded-bl-3xl bg-blue-500 text-white hover:bg-blue-700 duration-200"
              >
                <XMarkIcon className="size-6" />
                <span className="sr-only">Cerrar</span>
              </button>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
